import React from 'react';
import { ColumnProps, SortOrder, PaginationConfig } from 'antd/lib/table';
import UserContext from 'src/services/UserContext';
import { Sorting } from 'src/types/Sorting';
import { BugItem, BugsFilters } from '../../../types/Bugs';
import { Filters } from './hooks/useBugsFiltersFetch';

export const defaultPagination: PaginationConfig = {
  hideOnSinglePage: false,
  showSizeChanger: true,
  pageSizeOptions: ['10', '20', '50'],
  locale: { items_per_page: '' },
};

export type ColumnId = keyof BugItem;
export type ColumnSorting = Sorting<BugItem> & { field: ColumnId };

type StandardColumn = ColumnProps<BugItem>;
type FilterableColumn = StandardColumn & { dataIndex: ColumnId };

const renderBugLink = (bug: BugItem) => {
  const linkUrl = `https://ql2.atlassian.net/browse/${bug.bugId}`;
  const linkText = `${bug.bugId} ${bug.bugName}`;
  return (
    <a href={linkUrl} target="_blank" rel="noopener">
      {linkText}
    </a>
  );
};

const renderTimestamp = (timestamp: string | null) => {
  return timestamp !== null && timestamp !== 'null' ? UserContext.reformatUTCDate(timestamp) : 'N/A';
};
export const getColumns = (sorting: ColumnSorting, filters: Filters, currentFilters: BugsFilters) => {
  const columns: FilterableColumn[] = [
    {
      title: 'Bug ID / Name',
      dataIndex: 'bugId',
      render: (_, record) => renderBugLink(record),
      sorter: true,
    },
    {
      title: 'Bug Status',
      dataIndex: 'bugStatus',
      sorter: (a, b) => a.bugStatus.localeCompare(b.bugStatus),
      filters: filters.statuses || undefined,
      onFilter: () => true,
      filteredValue: currentFilters?.bugStatus?.split(','), // Set filteredValue dynamically
    },
    {
      title: 'Bug Priority',
      dataIndex: 'bugPriority',
      sorter: (a, b) => a.bugPriority.localeCompare(b.bugPriority),
      filters: filters.priorities ?? undefined,
      filterMultiple: true,
      onFilter: () => true,
    },
    {
      title: 'Organization',
      dataIndex: 'organization',
      sorter: (a, b) => (a.organization && b.organization ? a.organization.localeCompare(b.organization) : 0),
      filters: filters.organizations ?? undefined,
      filterMultiple: true,
      onFilter: () => true,
    },
    {
      title: 'Created Date',
      dataIndex: 'createdDate',
      sorter: (a: BugItem, b: BugItem) => compareValues(a.createdDate, b.createdDate),
      render: renderTimestamp,
    },
    {
      title: 'Due Date',
      dataIndex: 'dueDate',
      sorter: (a: BugItem, b: BugItem) => compareValues(a.dueDate, b.dueDate),
      render: renderTimestamp,
    },
  ];

  const sortOrder = (key: ColumnId): SortOrder | false => {
    return sorting.field === key ? sorting.order : false;
  };

  const filteredColumns = columns.map((column) => ({
    ...column,
    ...(column.sorter && { sortOrder: sortOrder(column.dataIndex) }),
  }));
  return filteredColumns;
};
const compareValues = (valueA: string | null | undefined, valueB: string | null | undefined) => {
  if (valueA == null && valueB == null) return 0;
  if (valueA == null) return -1;
  if (valueB == null) return 1;
  const numA = parseFloat(valueA);
  const numB = parseFloat(valueB);
  if (!isNaN(numA) && !isNaN(numB)) {
    return numA - numB;
  }
  return valueA.localeCompare(valueB);
};
